import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DesignPartnerModal from "../components/DesignPartnerModal";
import APIKeySection from "../components/APIKeySection";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowRight,
  Brain,
  Cpu,
  Zap,
  Database,
  GitBranch,
  Eye,
  AlertTriangle,
  Check,
  X,
  Plus,
  Terminal as TerminalIcon,
  Layers,
} from "lucide-react";
import { SiPython, SiJavascript, SiOpenai, SiAnthropic, SiPostgresql, SiRedis, SiFastapi } from "react-icons/si";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MemoryGraph from "@/components/MemoryGraph";
import TerminalDemo from "@/components/TerminalDemo";
import { initialNodes, initialLinks } from "@/lib/mockData";

// ────────────────────────────────────────────────────────────
// Helpers
const Section = ({ id, label, title, children, kicker }) => (
  <section id={id} className="border-t border-line-1 relative">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
      {label && (
        <div className="flex items-center gap-3 mb-6">
          <span className="w-6 h-px bg-cyber" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-cyber font-bold">{label}</span>
        </div>
      )}
      {title && (
        <h2 className="font-display font-black tracking-tight uppercase text-3xl md:text-5xl lg:text-6xl max-w-4xl mb-4 leading-[0.95]">
          {title}
        </h2>
      )}
      {kicker && <p className="max-w-2xl text-ink-300 mb-12">{kicker}</p>}
      {children}
    </div>
  </section>
);

// ────────────────────────────────────────────────────────────
// Hero
function Hero({ onAddNode, graphNodes, graphLinks, highlightId, user }) {
  return (
    <section className="relative overflow-hidden border-b border-line-1">
      {/* Backdrop grid */}
      <div className="absolute inset-0 bg-grid pointer-events-none opacity-60" />
      {/* Beam */}
      <div className="absolute left-1/3 top-0 bottom-0 w-px beam pointer-events-none" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 pb-10 md:pt-28 md:pb-16 relative">
        {/* Top tape */}
        <div className="flex items-center gap-3 mb-10">
          <span className="text-[10px] tracking-[0.3em] uppercase text-amber border border-amber/40 px-2 py-0.5">
            early access · live
          </span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-ink-500">
            // the institutional memory layer for AI-first companies
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Left col */}
          <div className="lg:col-span-6">
            <h1
              data-testid="hero-h1"
              className="font-display font-black tracking-[-0.04em] uppercase leading-[0.85] text-[14vw] md:text-[7.5vw] lg:text-[6.4vw]"
            >
              Cure
              <br />
              <span className="text-brand text-glow">AI</span> amnesia
              <span className="text-amber text-glow-amber">_</span>
            </h1>

            <p className="mt-8 max-w-xl text-ink-300 text-base md:text-lg leading-relaxed">
              Mnemo is the persistent cognitive layer for your agents. They{" "}
              <span className="text-ink-100">capture</span> every task,{" "}
              <span className="text-cyber">reflect</span> on every outcome, and{" "}
              <span className="text-amber">recall</span> the lessons that made them better — across
              every run, every session, every deploy.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              {user ? (
                <Link
                  to="/dashboard"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center gap-2 bg-brand text-void px-5 py-3 text-[12px] font-bold tracking-[0.2em] uppercase hover:shadow-[0_0_24px_rgba(0,229,255,0.45)] transition-shadow"
                >
                  Open Dashboard <ArrowRight size={16} strokeWidth={2.4} />
                </Link>
              ) : (
                <>
                  <Link
                    to="/signup"
                    data-testid="hero-cta-primary"
                    className="group inline-flex items-center gap-2 bg-brand text-void px-5 py-3 text-[12px] font-bold tracking-[0.2em] uppercase hover:shadow-[0_0_24px_rgba(0,229,255,0.45)] transition-shadow"
                  >
                    Get started free <ArrowRight size={16} strokeWidth={2.4} />
                  </Link>
                  <Link
                    to="/login"
                    className="group inline-flex items-center gap-2 border border-line-1 text-ink-100 px-5 py-3 text-[12px] font-bold tracking-[0.2em] uppercase hover:border-cyber hover:text-cyber transition-colors"
                  >
                    Login
                  </Link>
                </>
              )}
              <a
                href="#sdk"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-line-2 text-ink-100 px-5 py-3 text-[12px] font-bold tracking-[0.2em] uppercase hover:border-ink-100"
              >
                <SiPython size={14} /> pip install mnemonic-sdk
              </a>
              <a
                href="#sdk"
                className="inline-flex items-center gap-2 border border-line-2 text-ink-100 px-5 py-3 text-[12px] font-bold tracking-[0.2em] uppercase hover:border-ink-100"
              >
                <SiJavascript size={14} /> npm install @mnemonicai.official/sdk-js
              </a>
            </div>
            
            <p className="mt-4 text-sm text-ink-500">
              Early access now open. Build agents that learn from every execution. Free tier. No credit card.
            </p>

            {/* Stats */}
            <div className="mt-14 grid grid-cols-3 gap-4 max-w-xl">
              {[
                { k: "2,700+", v: "lessons extracted" },
                { k: "37,000+", v: "events processed" },
                { k: "10", v: "connectors live" },
              ].map((s) => (
                <div key={s.k} className="border-l border-cyber pl-3">
                  <div className="font-display font-black text-2xl md:text-3xl text-ink-100">
                    {s.k}
                  </div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-ink-500 mt-1">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right col - Graph */}
          <div className="lg:col-span-6 relative">
            <div className="relative border border-line-2 bg-surface vignette">
              <div className="absolute -top-px -left-px w-3 h-3 border-t border-l border-cyber" />
              <div className="absolute -top-px -right-px w-3 h-3 border-t border-r border-cyber" />
              <div className="absolute -bottom-px -left-px w-3 h-3 border-b border-l border-cyber" />
              <div className="absolute -bottom-px -right-px w-3 h-3 border-b border-r border-cyber" />
              <MemoryGraph
                nodes={graphNodes}
                links={graphLinks}
                height={520}
                highlightId={highlightId}
              />
            </div>
            <button
              data-testid="hero-add-memory"
              onClick={onAddNode}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-line-1 hover:border-cyber text-cyber text-[11px] tracking-[0.3em] uppercase py-2.5 hover:bg-cyber/5"
            >
              <Plus size={12} strokeWidth={2} /> simulate · capture new memory
            </button>
          </div>
        </div>

        {/* Logo strip */}
        <div className="mt-16 border-t border-line-1 pt-6 flex items-center gap-10 overflow-hidden">
          <span className="text-[10px] tracking-[0.3em] uppercase text-ink-500 shrink-0">
            wired with /
          </span>
          <div className="flex items-center gap-12 text-ink-300 animate-marquee whitespace-nowrap">
            {[
              [SiPython, "Python"],
              [SiOpenai, "OpenAI"],
              [SiAnthropic, "Anthropic"],
              [SiFastapi, "FastAPI"],
              [SiPostgresql, "Postgres + pgvector"],
              [SiRedis, "Redis"],
              [SiPython, "Python"],
              [SiOpenai, "OpenAI"],
              [SiAnthropic, "Anthropic"],
              [SiFastapi, "FastAPI"],
              [SiPostgresql, "Postgres + pgvector"],
              [SiRedis, "Redis"],
            ].map(([Icon, label], i) => (
              <span key={i} className="inline-flex items-center gap-2 text-sm">
                <Icon size={16} /> <span className="text-[11px] tracking-[0.2em] uppercase">{label}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────
// Problem section
function ProblemSection() {
  const stateless = [
    "Forgets every win after the session ends",
    "Repeats the same mistake on run #14",
    "Burns tokens re-deriving solved patterns",
    "Cannot share insight with sibling agents",
    "Confidence is a fiction — nothing is measured",
  ];
  const mnemo = [
    "Persists outcomes across runs and deploys",
    "Promotes successful patterns into procedures",
    "Recalls relevant lessons before each task",
    "Detects contradictions between lessons",
    "Confidence decays, calibrates, compounds",
  ];

  return (
    <Section
      id="problem"
      label="01 · the problem"
      title={
        <>
          Your agents are <span className="text-hotpink">brilliant</span>
          <br />
          and <span className="text-amber">amnesiac</span>.
        </>
      }
      kicker="Your agent solved the Railway deployment bug yesterday. Today it tried the same broken config again. You just paid GPT-4 $12 to re-learn what it already knew. This happens 40 times a day. MnemonicAI fixes it."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 border border-line-2">
        {/* Stateless */}
        <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-line-2 relative">
          <div className="absolute top-0 left-0 right-0 h-1 warning-stripe opacity-80" />
          <div className="flex items-center gap-2 mb-4 mt-4">
            <AlertTriangle size={16} className="text-hotpink" strokeWidth={1.5} />
            <span className="text-[11px] tracking-[0.3em] uppercase text-hotpink">
              stateless agent
            </span>
          </div>
          <h3 className="font-display font-bold text-2xl mb-6 text-ink-100">
            Goldfish with a GPU.
          </h3>
          <ul className="space-y-3">
            {stateless.map((s) => (
              <li key={s} className="flex items-start gap-3 text-ink-300">
                <X size={16} className="text-hotpink mt-1 shrink-0" strokeWidth={2} />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* Mnemo */}
        <div className="p-8 md:p-10 relative">
          <div className="absolute top-0 left-0 right-0 h-1 bg-cyber" />
          <div className="flex items-center gap-2 mb-4 mt-4">
            <Brain size={16} className="text-cyber" strokeWidth={1.5} />
            <span className="text-[11px] tracking-[0.3em] uppercase text-cyber">
              mnemo agent
            </span>
          </div>
          <h3 className="font-display font-bold text-2xl mb-6 text-ink-100">
            Compounding cognition.
          </h3>
          <ul className="space-y-3">
            {mnemo.map((s) => (
              <li key={s} className="flex items-start gap-3 text-ink-300">
                <Check size={16} className="text-cyber mt-1 shrink-0" strokeWidth={2.4} />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────
// Use Cases
function UseCases() {
  const cases = [
    {
      title: "Customer Support Automation",
      icon: Cpu,
      before: "Agents escalate common tickets repeatedly",
      after: "Learn from successful resolutions, auto-apply patterns",
      metric: "Potential cost reduction",
      color: "text-cyber"
    },
    {
      title: "Code Generation Agents",
      icon: TerminalIcon,
      before: "Multiple retries, repeated compilation errors",
      after: "Remember what worked, reduce trial-and-error",
      metric: "Fewer wasted tokens",
      color: "text-amber"
    },
    {
      title: "DevOps Automation",
      icon: Database,
      before: "Same incidents repeat monthly",
      after: "Auto-create prevention rules from past fixes",
      metric: "Learn from incidents",
      color: "text-hotpink"
    }
  ];

  return (
    <Section
      id="use-cases"
      label="Use cases"
      title={
        <>
          Built for <span className="text-cyber">production</span> agents.
        </>
      }
      kicker="Connect your tools. Mnemonic watches what your company does and automatically builds a brain your AI agents can query."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {cases.map((c, i) => (
          <motion.div
            key={c.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.4, delay: i * 0.1 }}
            className="border border-line-2 bg-surface p-6 hover:border-line-1 transition-colors"
          >
            <c.icon size={24} className={`${c.color} mb-4`} strokeWidth={1.5} />
            <h4 className="font-display font-bold text-xl mb-4">{c.title}</h4>
            
            <div className="space-y-4 mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-hotpink mb-1">Before</div>
                <div className="text-sm text-ink-300">{c.before}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-cyber mb-1">After</div>
                <div className="text-sm text-ink-300">{c.after}</div>
              </div>
            </div>
            
            <div className="pt-4 border-t border-line-1">
              <div className={`text-2xl font-display font-black ${c.color}`}>
                {c.metric}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      
      <div className="mt-12 text-center">
        <p className="text-sm text-ink-400 mb-4">
          Perfect for AI-first companies building with any LLM model
        </p>
        <div className="flex items-center justify-center gap-4 text-xs text-ink-500">
          <span>• Coding agents</span>
          <span>• Support agents</span>
          <span>• Research agents</span>
          <span>• Data analysts</span>
          <span>• DevOps automation</span>
        </div>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────
// How it works
function HowItWorks() {
  const steps = [
    {
      n: "01",
      icon: Eye,
      title: "Capture",
      copy: "Every task, action, output, outcome — streamed to Mnemo as structured events. Trace as a first-class citizen.",
      color: "text-amber",
      border: "border-amber/40",
    },
    {
      n: "02",
      icon: Brain,
      title: "Reflect",
      copy: "The Reflection Engine distills raw traces into atomic lessons and reusable procedures. Confidence scores. Contradiction detection.",
      color: "text-cyber",
      border: "border-cyber/40",
    },
    {
      n: "03",
      icon: Zap,
      title: "Recall",
      copy: "Before the agent acts, semantic search injects the most relevant lessons + procedures into the prompt. Sub-50ms.",
      color: "text-hotpink",
      border: "border-hotpink/40",
    },
  ];

  return (
    <Section
      id="how"
      label="02 · the loop"
      title={
        <>
          Capture → <span className="text-cyber">Reflect</span> → Recall.
          <br />
          Forever.
        </>
      }
      kicker="Two SDK calls bracket every agent invocation. The reflection engine does the heavy lifting offline. Your agent gets sharper while it sleeps."
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Steps */}
        <div className="lg:col-span-5 space-y-4">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className={`group border border-line-2 hover:${s.border} bg-surface p-6 transition-colors relative`}
            >
              <div className="flex items-start gap-5">
                <div className={`text-4xl font-display font-black ${s.color} leading-none`}>
                  {s.n}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <s.icon size={16} className={s.color} strokeWidth={1.5} />
                    <h4 className="font-display font-bold text-lg uppercase tracking-tight">
                      {s.title}
                    </h4>
                  </div>
                  <p className="text-ink-300 text-sm leading-relaxed">{s.copy}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Terminal */}
        <div className="lg:col-span-7" id="sdk">
          <TerminalDemo />
          <div className="mt-3 flex justify-between text-[10px] tracking-[0.3em] uppercase text-ink-500">
            <span>// drop-in for openai · anthropic · langchain</span>
            <span className="text-cyber">live · auto-replay</span>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────
// MCP Section
function MCPSection() {
  return (
    <Section
      id="mcp"
      label="03 · mcp server"
      title={
        <>
          Claude Desktop.{" "}
          <span className="text-cyber">Zero code.</span>
        </>
      }
      kicker="Install Mnemonic as an MCP server and Claude will recall past lessons and capture new ones automatically — no agent code changes required."
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* What it does */}
        <div className="space-y-4">
          {[
            { icon: Brain, color: "text-cyber", title: "Auto recall", copy: "Before every task, Claude queries Mnemonic for relevant lessons and injects them into its context automatically." },
            { icon: Database, color: "text-terminal", title: "Auto capture", copy: "After every task, Claude captures what happened. The reflection engine extracts lessons while you sleep." },
            { icon: Zap, color: "text-amber", title: "No rewrites", copy: "Works with Claude Desktop and Cursor today. No changes to your prompts, workflows, or existing code." },
          ].map((item) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.4 }}
              className="border border-line-2 bg-surface p-5 flex gap-4"
            >
              <item.icon size={18} className={`${item.color} mt-0.5 shrink-0`} strokeWidth={1.5} />
              <div>
                <div className="font-display font-bold uppercase text-sm tracking-tight mb-1">{item.title}</div>
                <p className="text-ink-400 text-xs leading-relaxed">{item.copy}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Config block */}
        <div className="border border-line-1 bg-surface p-6 space-y-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">1 · install</div>
          <div className="bg-void border border-line-2 p-3 font-mono text-xs text-terminal">
            npm install -g @mnemonicai.official/mcp
          </div>

          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">
            2 · add to claude_desktop_config.json
          </div>
          <div className="bg-void border border-line-2 p-3 font-mono text-xs text-ink-300 whitespace-pre leading-relaxed">{`{
  "mcpServers": {
    "mnemonic": {
      "command": "npx",
      "args": ["-y", "@mnemonicai.official/mcp"],
      "env": {
        "MNEMONIC_API_KEY": "mnemo_sk_...",
        "MNEMONIC_AGENT_ID": "claude-desktop"
      }
    }
  }
}`}</div>

          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">3 · restart claude desktop</div>
          <p className="text-xs text-ink-400">
            That's it.{" "}
            <span className="text-terminal">mnemonic_recall</span> and{" "}
            <span className="text-terminal">mnemonic_capture</span> will appear in Claude's tool list.
          </p>

          <a
            href="https://mnemonic.nishantvanawala6118.workers.dev/signup"
            className="inline-flex items-center gap-2 bg-cyber text-void px-4 py-2 text-xs font-bold tracking-wider uppercase hover:shadow-[0_0_16px_rgba(0,229,255,0.4)] transition-shadow"
          >
            Get your API key <ArrowRight size={13} />
          </a>
        </div>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────
// Value & ROI
function ValueMetrics() {
  return (
    <Section
      label="What's built"
      title={
        <>
          A brain that builds <span className="text-cyber">itself</span>.
        </>
      }
      kicker="No manual curation. Connect your tools and Mnemonic automatically extracts what your company knows from the work it's already doing."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            metric: "2,700+",
            label: "Lessons extracted",
            detail: "Automatically distilled from events — no human wrote a single one."
          },
          {
            metric: "37,000+",
            label: "Events processed",
            detail: "Incidents, PRs, tickets, Slack threads — all reflected on automatically."
          },
          {
            metric: "5,400+",
            label: "Knowledge connections",
            detail: "Lessons linked by shared entities. The brain knows what relates to what."
          },
          {
            metric: "10",
            label: "Connectors live",
            detail: "Slack, GitHub, Jira, Linear, Notion, PagerDuty, HubSpot, Zapier and more."
          }
        ].map((item, i) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
            className="border border-line-2 bg-surface p-6"
          >
            <div className="text-4xl font-display font-black text-cyber leading-none mb-3">
              {item.metric}
            </div>
            <div className="font-display font-bold text-base mb-2">{item.label}</div>
            <div className="text-sm text-ink-400 leading-relaxed">{item.detail}</div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────
// Reflection engine deep-dive
function ReflectionEngine() {
  return (
    <Section
      id="engine"
      label="03 · the moat"
      title={<>The Reflection Engine.</>}
      kicker="Raw traces are cheap. Distilled, scored, deduplicated, and contradiction-aware lessons are not. This is where the moat lives."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          {
            icon: Layers,
            title: "Atomic lessons",
            body: "Outcome → single-sentence insight, embedded with pgvector. Confidence starts at 0.5 and updates on each recall hit/miss.",
          },
          {
            icon: GitBranch,
            title: "Promoted procedures",
            body: "When the same lesson cluster keeps winning, it's promoted to a named procedure — a reusable workflow your agents can replay.",
          },
          {
            icon: Database,
            title: "Contradiction detection",
            body: "Two lessons disagree? The engine flags it, surfaces both, and lets the operator (or the next reflection) resolve.",
          },
        ].map((c, i) => (
          <motion.div
            key={c.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
            className="border border-line-2 bg-surface p-6 hover:border-cyber/40 transition-colors group"
          >
            <c.icon size={20} className="text-cyber mb-4" strokeWidth={1.5} />
            <h4 className="font-display font-bold uppercase tracking-tight text-lg mb-2">
              {c.title}
            </h4>
            <p className="text-ink-300 text-sm leading-relaxed">{c.body}</p>
            <div className="mt-5 h-px bg-line-1 group-hover:bg-cyber transition-colors" />
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────
// Comparison Table
function Comparison() {
  const features = [
    { name: "Learns from company behavior", mnemo: true, notion: false, mem0: false, glean: false },
    { name: "Auto-extracts lessons (no curation)", mnemo: true, notion: false, mem0: "partial", glean: false },
    { name: "Connector ingestion (Slack, Jira, GitHub…)", mnemo: true, notion: false, mem0: false, glean: true },
    { name: "Knowledge graph", mnemo: true, notion: false, mem0: true, glean: true },
    { name: "AI agent SDK", mnemo: true, notion: false, mem0: true, glean: false },
    { name: "Contradiction detection", mnemo: true, notion: false, mem0: false, glean: false },
    { name: "Free / self-serve tier", mnemo: true, notion: true, mem0: true, glean: false },
  ];

  const renderCheck = (val) => {
    if (val === true) return <Check size={18} className="text-cyber" strokeWidth={2.4} />;
    if (val === false) return <X size={18} className="text-ink-600" strokeWidth={2} />;
    if (val === "partial") return <span className="text-amber text-xs">⚠</span>;
    return <span className="text-xs text-ink-400">{val}</span>;
  };

  return (
    <Section
      id="comparison"
      label="02 · vs alternatives"
      title={
        <>
          Why MnemonicAI vs{" "}
          <span className="text-ink-500">everything else</span>?
        </>
      }
      kicker="Notion needs humans to write things. Mem0 stores chat history. Glean searches existing docs. Mnemonic learns from what your company actually does."
    >
      <div className="overflow-x-auto">
        <table className="w-full border border-line-2">
          <thead>
            <tr className="border-b border-line-2 bg-surface">
              <th className="text-left p-4 text-sm font-mono uppercase tracking-wider text-ink-500">
                Feature
              </th>
              <th className="p-4 text-sm font-mono uppercase tracking-wider text-cyber border-l border-line-2">
                Mnemonic
              </th>
              <th className="p-4 text-sm font-mono uppercase tracking-wider text-ink-400 border-l border-line-2">
                Notion AI
              </th>
              <th className="p-4 text-sm font-mono uppercase tracking-wider text-ink-400 border-l border-line-2">
                Mem0
              </th>
              <th className="p-4 text-sm font-mono uppercase tracking-wider text-ink-400 border-l border-line-2">
                Glean
              </th>
            </tr>
          </thead>
          <tbody>
            {features.map((f, i) => (
              <tr key={f.name} className={i % 2 === 0 ? "bg-base" : "bg-surface"}>
                <td className="p-4 text-sm text-ink-300 border-t border-line-2">
                  {f.name}
                </td>
                <td className="p-4 text-center border-t border-l border-line-2 bg-cyber/5">
                  {renderCheck(f.mnemo)}
                </td>
                <td className="p-4 text-center border-t border-l border-line-2">
                  {renderCheck(f.notion)}
                </td>
                <td className="p-4 text-center border-t border-l border-line-2">
                  {renderCheck(f.mem0)}
                </td>
                <td className="p-4 text-center border-t border-l border-line-2">
                  {renderCheck(f.glean)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-line-1 bg-surface p-4">
          <div className="text-xs uppercase tracking-wider text-ink-500 mb-2">The Difference</div>
          <div className="text-sm text-ink-300">
            Vector DBs store embeddings. MnemonicAI stores <span className="text-cyber">lessons that get better</span> with usage.
          </div>
        </div>
        <div className="border border-line-1 bg-surface p-4">
          <div className="text-xs uppercase tracking-wider text-ink-500 mb-2">Quality Over Time</div>
          <div className="text-sm text-ink-300">
            Good lessons rank higher. Bad ones fade. This is <span className="text-cyber">learning</span>, not just retrieval.
          </div>
        </div>
        <div className="border border-line-1 bg-surface p-4">
          <div className="text-xs uppercase tracking-wider text-ink-500 mb-2">Network Effects</div>
          <div className="text-sm text-ink-300">
            Community tier shares lessons globally. <span className="text-cyber">Everyone gets smarter</span> together.
          </div>
        </div>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────
// Social Proof
function SocialProof() {
  return (
    <Section
      label="Early Access"
      title={
        <>
          Built, tested, ready for first users.
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="border border-line-2 bg-surface p-6">
          <div className="text-4xl font-display font-black text-cyber mb-2">2,700+</div>
          <div className="text-sm text-ink-400">Lessons extracted automatically</div>
        </div>
        <div className="border border-line-2 bg-surface p-6">
          <div className="text-4xl font-display font-black text-terminal mb-2">37,000+</div>
          <div className="text-sm text-ink-400">Events processed from real sources</div>
        </div>
        <div className="border border-line-2 bg-surface p-6">
          <div className="text-4xl font-display font-black text-amber mb-2">10</div>
          <div className="text-sm text-ink-400">Connectors — Slack, GitHub, Jira and more</div>
        </div>
      </div>

      <div className="border border-line-2 bg-surface p-8">
        <div className="max-w-3xl">
          <div className="text-sm font-mono text-cyber mb-4">// EARLY ACCESS</div>
          <p className="text-ink-300 text-lg leading-relaxed mb-6">
            Mnemonic is live. The API is running, the SDK is on npm, and the brain is
            already processing real data. Looking for early users who are building
            production AI agents and want their company's knowledge working for them.
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              onClick={() => setIsDesignPartnerModalOpen(true)}
              className="inline-flex items-center gap-2 bg-cyber text-void px-5 py-3 text-[12px] font-bold tracking-[0.2em] uppercase hover:shadow-[0_0_24px_rgba(0,229,255,0.45)] transition-shadow"
            >
              GET EARLY ACCESS <ArrowRight size={16} />
            </button>
            <span className="text-sm text-ink-500">Solo founder • Ship fast • Talk to me directly</span>
          </div>
        </div>
      </div>

      <div className="mt-12 text-center">
        <div className="inline-flex items-center gap-3 bg-surface border border-line-2 px-6 py-3">
          <Brain size={20} className="text-cyber" />
          <span className="text-sm text-ink-300">
            Built for developers building production AI agents
          </span>
        </div>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────
// Pricing
function Pricing() {
  const tiers = [
    {
      name: "Free",
      price: "$0",
      period: "/forever",
      tag: "Early Access",
      features: [
        "Full API access",
        "Unlimited agents",
        "SDK + MCP server",
        "Community support",
      ],
      cta: "Get started free",
      ctaTestId: "pricing-cta-free",
      highlight: false,
      savings: null
    },
    {
      name: "Pro",
      price: "TBD",
      period: "",
      tag: "Coming soon",
      features: [
        "Everything in Free",
        "Priority connector syncs",
        "Advanced analytics dashboard",
        "Email support",
      ],
      cta: "Join waitlist",
      ctaTestId: "pricing-cta-pro",
      highlight: true,
      savings: "Pricing set with early users"
    },
    {
      name: "Enterprise",
      price: "Talk",
      period: "/to us",
      tag: "Custom",
      features: [
        "Dedicated deployment",
        "Custom connectors",
        "SLA + onboarding",
        "Direct line to founder",
      ],
      cta: "Get in touch",
      ctaTestId: "pricing-cta-enterprise",
      highlight: false,
      savings: null
    },
  ];

  return (
    <Section
      id="pricing"
      label="03 · pricing"
      title={
        <>
          Free as in <span className="text-cyber">git clone</span>.
          <br />
          Paid as in <span className="text-amber">git push</span>.
        </>
      }
      kicker="Free while we're early. Pricing will be set together with the first paying users — no surprises."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 border border-line-2">
        {tiers.map((t, i) => (
          <div
            key={t.name}
            className={`p-8 relative ${
              i < tiers.length - 1 ? "md:border-r border-line-2" : ""
            } ${i < 2 ? "border-b md:border-b-0 border-line-2" : ""} ${
              t.highlight ? "bg-gradient-to-b from-cyber/5 to-transparent" : ""
            }`}
            data-testid={`pricing-tier-${t.name.toLowerCase()}`}
          >
            {t.highlight && (
              <>
                <div className="absolute top-0 left-0 right-0 h-px bg-cyber" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1 bg-cyber text-void text-[10px] tracking-[0.3em] uppercase font-bold">
                  · most loved ·
                </div>
              </>
            )}
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500 mb-2">
              {t.tag}
            </div>
            <div className="font-display font-black uppercase text-3xl mb-3">{t.name}</div>
            <div className="flex items-baseline gap-1 mb-6">
              <span className="font-display font-black text-5xl text-ink-100">{t.price}</span>
              <span className="text-ink-500 text-sm">{t.period}</span>
            </div>
            <ul className="space-y-3 mb-6 min-h-[180px]">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-ink-300 text-sm">
                  <span className="text-cyber mt-1">›</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {t.savings && (
              <div className="mb-6 p-3 bg-cyber/5 border border-cyber/20 rounded">
                <div className="text-xs text-cyber font-mono">{t.savings}</div>
              </div>
            )}
            <button
              data-testid={t.ctaTestId}
              className={`w-full py-3 text-[11px] font-bold tracking-[0.3em] uppercase border transition-all ${
                t.highlight
                  ? "bg-cyber text-void border-cyber hover:shadow-[0_0_18px_rgba(0,229,255,0.5)]"
                  : "border-line-2 text-ink-100 hover:border-ink-100"
              }`}
            >
              {t.cta} →
            </button>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────
// FAQ
function FAQ() {
  const faqs = [
    {
      q: "How is this different from a vector database?",
      a: "Vector DBs (Pinecone, Weaviate) store embeddings. MnemonicAI stores lessons that get better with usage. Quality scores compound, bad lessons fade, contradictions get flagged. It's learning, not just retrieval."
    },
    {
      q: "What if my agent makes the same mistake twice?",
      a: "That's exactly what we prevent. When an approach fails, the lesson's quality score drops. When it succeeds, the score climbs. After enough data, bad patterns stop getting recalled."
    },
    {
      q: "Do I need to change my existing agent code?",
      a: "Minimal changes. Two SDK calls: one before execution (recall), one after (capture). Or use the MCP server with Claude Desktop / Cursor — zero code changes needed. The reflection engine runs async in the background."
    },
    {
      q: "How fast is recall?",
      a: "Sub-50ms even with 100K+ lessons. We use pgvector with IVFFlat indexes. First call is ~20ms, subsequent calls hit Redis cache at 2-5ms."
    },
    {
      q: "What's the network effect?",
      a: "Community tier lessons are public. When your agent solves a problem, other agents benefit. Pro/Enterprise tiers keep lessons private for competitive advantage."
    },
    {
      q: "Can I self-host?",
      a: "Yes! The core engine is Apache-2.0. docker-compose gets you up in minutes. Mnemo Cloud is the hosted option if you want zero DevOps."
    },
    {
      q: "What happens if I hit my reflection limit?",
      a: "We won't cut you off mid-month. You'll get a heads-up email, and we'll help you upgrade or optimize. Most customers use 20-40% of their limit."
    },
    {
      q: "Do lessons work across different models?",
      a: "Yes. Lessons are model-agnostic. A lesson learned with GPT-4 works for Claude or your fine-tuned model. The insight is the value, not the model."
    }
  ];

  return (
    <Section
      id="faq"
      label="04 · faq"
      title={<>Questions? <span className="text-cyber">Answered.</span></>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {faqs.map((faq, i) => (
          <div
            key={i}
            className="border border-line-2 bg-surface p-6 hover:border-line-1 transition-colors"
          >
            <h4 className="font-display font-bold text-lg mb-3 text-ink-100">
              {faq.q}
            </h4>
            <p className="text-sm text-ink-400 leading-relaxed">{faq.a}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <p className="text-sm text-ink-400 mb-4">Still have questions?</p>
        <a
          href="mailto:mnemonicai.official@gmail.com"
          className="inline-flex items-center gap-2 text-cyber hover:underline text-sm"
        >
          Email us → mnemonicai.official@gmail.com
        </a>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────
// Final CTA
function FinalCta() {
  return (
    <section className="relative border-t border-line-1 overflow-hidden">
      <div className="absolute inset-0 bg-grid-fine opacity-50 pointer-events-none" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="text-[10px] tracking-[0.3em] uppercase text-cyber mb-4">
              [ next.run ]
            </div>
            <h2 className="font-display font-black uppercase tracking-tight text-4xl md:text-6xl leading-[0.9]">
              Stop training. <br />
              Start <span className="text-cyber text-glow">remembering</span>.
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <Link
              to="/demo"
              data-testid="footer-cta-dashboard"
              className="inline-flex items-center justify-between bg-cyber text-void px-6 py-5 text-sm font-bold tracking-[0.2em] uppercase hover:shadow-[0_0_24px_rgba(0,229,255,0.45)] transition-shadow"
            >
              Open the dashboard <ArrowRight size={18} strokeWidth={2.4} />
            </Link>
            <a
              href="#sdk"
              data-testid="footer-cta-sdk"
              className="inline-flex items-center justify-between border border-line-2 px-6 py-5 text-sm font-bold tracking-[0.2em] uppercase text-ink-100 hover:border-ink-100"
            >
              <span className="inline-flex items-center gap-2">
                <TerminalIcon size={16} /> install the sdk
              </span>
              <span className="text-ink-500 text-xs font-mono">pip install mnemonic-sdk</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────
// Page
export default function Landing() {
  // Live, mutable graph data
  const { user } = useAuth();
  const [nodes, setNodes] = useState(() => initialNodes.map((n) => ({ ...n })));
  const [links, setLinks] = useState(() => initialLinks.map((l) => ({ ...l })));
  const [highlightId, setHighlightId] = useState(null);
  const [isDesignPartnerModalOpen, setIsDesignPartnerModalOpen] = useState(false);

  // Auto-pulse a random node every few seconds
  useEffect(() => {
    const t = setInterval(() => {
      const rand = nodes[Math.floor(Math.random() * nodes.length)];
      if (rand) setHighlightId(rand.id);
    }, 2400);
    return () => clearInterval(t);
  }, [nodes]);

  const lessonPool = useMemo(
    () => [
      { id: "lesson:retry-jitter", label: "Add jitter to exp. backoff", group: "lesson" },
      { id: "lesson:idempotency-key", label: "Always idempotency keys", group: "lesson" },
      { id: "lesson:env-not-args", label: "Secrets in env, not argv", group: "lesson" },
      { id: "lesson:cache-warm", label: "Warm cache before peak", group: "lesson" },
      { id: "lesson:stream-dont-batch", label: "Stream don't batch >10MB", group: "lesson" },
      { id: "lesson:typed-errors", label: "Typed errors > strings", group: "lesson" },
    ],
    [],
  );

  const addNode = () => {
    const fresh = lessonPool[Math.floor(Math.random() * lessonPool.length)];
    const stamp = Date.now().toString(36).slice(-4);
    const newId = `${fresh.id}-${stamp}`;
    const newNode = { ...fresh, id: newId, confidence: 0.5 };
    // Link it to a random existing task or procedure
    const targets = nodes.filter((n) => n.group !== "lesson");
    const target = targets[Math.floor(Math.random() * targets.length)];
    setNodes((n) => [...n, newNode]);
    setLinks((l) => [...l, { source: newId, target: target.id }]);
    setHighlightId(newId);
  };

  return (
    <div className="App noise min-h-screen bg-void text-ink-100 selection:bg-cyber selection:text-void">
      <Header />
      <main>
        <Hero
          onAddNode={addNode}
          graphNodes={nodes}
          graphLinks={links}
          highlightId={highlightId}
          user={user}
        />
        <ProblemSection />
        <UseCases />
        <HowItWorks />
        <MCPSection />
        <ValueMetrics />
        <ReflectionEngine />
        <Comparison />
        <SocialProof />
        <Pricing />
        <APIKeySection />
        <FAQ />
        <FinalCta />
      </main>
      <Footer />
      <DesignPartnerModal 
        isOpen={isDesignPartnerModalOpen} 
        onClose={() => setIsDesignPartnerModalOpen(false)}
      />
    </div>
  );
}
